import {type ICSSWriter} from '@packages/systems/style/writers';
import {entries} from '@packages/utilities/key-map';
import {
  toDefaultClassName,
  conditionalDefaultClassName,
  createDefaultRulesFromConfig,
} from './default-styles';
import type {DefaultStyleConfig} from '@packages/systems/style/types';
import {
  Component,
  type NormalizedAtomConfig,
  type ContextType,
} from '@packages/systems/wfdl';

import {createMediaRulesFromBreakpoints} from './utils';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {
  WebflowEnvironments,
  type Environment,
} from '@packages/systems/core-constants';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {EvaluatedNodeData} from '@packages/utilities/data-utils';

export const atomConfigToDefaultStyleConfig = (
  atom: NormalizedAtomConfig
): DefaultStyleConfig => ({
  defaultKey: atom.atomKey,
  pluginKey: atom.pluginKey,
  className: atom.className,
  style: atom.style,
});

export const symbolRootToDefaultStyleConfig = (
  defaultKey: string,
  symbol: Component.ComponentType
): DefaultStyleConfig => {
  const style = Component.getStyle(symbol);
  if (style) {
    return {defaultKey, pluginKey: 'symbol', style};
  }
  return {defaultKey, pluginKey: 'symbol'};
};

export function jsonNodeSymbolRootToDefaultStyleConfig(
  symbol: JSONNode
): DefaultStyleConfig {
  if (symbol.data && symbol.data.sym && symbol.data.sym.style) {
    return {
      defaultKey: symbol._id,
      pluginKey: 'symbol',
      style: symbol.data.sym.style,
    };
  }
  return {defaultKey: symbol._id, pluginKey: 'symbol'};
}

export class DefaultStyle<T> {
  writer: ICSSWriter<T>;
  breakpoints: Array<Breakpoint>;
  env: string;
  constructor(
    writer: ICSSWriter<T>,
    breakpoints: Array<Breakpoint>,
    env: Environment
  ) {
    this.writer = writer;
    this.breakpoints = breakpoints;
    this.env = env;
  }

  defaultStyleConfigs: Map<string, DefaultStyleConfig> = new Map();

  addDefaultStyle(defaultKey: string, defaultStyleConfig: DefaultStyleConfig) {
    if (!this.defaultStyleConfigs.has(defaultKey)) {
      this.defaultStyleConfigs.set(defaultKey, defaultStyleConfig);
    }
  }

  commit(): T {
    const presetMediaRules = createMediaRulesFromBreakpoints(this.breakpoints);

    for (const [, defaultStyleConfig] of this.defaultStyleConfigs) {
      const defaultStyleRules = createDefaultRulesFromConfig(
        defaultStyleConfig,
        {emulatePseudos: this.env === WebflowEnvironments.DESIGNER}
      );

      for (const key of Object.keys(defaultStyleRules)) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<BreakpointID, any[]>>'.
        const rules = defaultStyleRules[key];

        if (key === 'main') {
          // @ts-expect-error - TS7006 - Parameter 'rule' implicitly has an 'any' type.
          rules.forEach((rule) => this.writer.addRule(rule));
          continue;
        }

        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'BreakpointID'.
        const mediaRule = presetMediaRules.get(key);

        if (mediaRule) {
          mediaRule.rules.push(...rules);
        }
      }
    }

    for (const [, mediaRule] of presetMediaRules) {
      if (mediaRule && mediaRule.rules.length) {
        this.writer.addRule(mediaRule);
      }
    }

    return this.writer.commit();
  }
}

/*
 * EvalElement class + style handling logic
 */

interface WriteOnlyArray<T> {
  push(...items: Array<T>): number;
}

export function getDefaultClasses(
  data: EvaluatedNodeData,
  pluginContext: ContextType,
  pluginConfig: NormalizedAtomConfig
): Array<string> {
  const classes: WriteOnlyArray<string> = [];
  addDefaultClasses(
    data,
    pluginContext,
    atomConfigToDefaultStyleConfig(pluginConfig),
    classes
  );
  // Loosen the WriteOnly refinement for the output type.
  return classes as any;
}

function addDefaultClasses(
  data: EvaluatedNodeData,
  pluginContext: ContextType,
  pluginConfig: DefaultStyleConfig,
  // NOTE: The `classes` argument will be mutated.
  classes: WriteOnlyArray<string>
) {
  const styleConfig = pluginConfig.style;

  // Bail out if there is nothing to do.
  if (styleConfig == null) {
    return;
  }

  const pluginClassName = toDefaultClassName(pluginConfig);
  classes.push(pluginClassName);

  // Check if any conditionals specified in the style config are active given
  // the node and current pluginContext.
  const conditionalConfig = styleConfig.conditional;
  if (conditionalConfig) {
    for (const [conditionalKey, valueConfig] of entries(conditionalConfig)) {
      // Check if the node.data has the conditionalKey.
      // If it does, check if the value of the conditionalKey exists in the valueConfig.
      // If so, we have a match and need to calculate a className.
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      let conditionalValue = data[conditionalKey];
      if (conditionalValue == null) {
        conditionalValue = pluginContext.get(conditionalKey);
      }
      if (
        conditionalValue != null &&
        valueConfig.hasOwnProperty(conditionalValue)
      ) {
        // Calculate className.
        classes.push(
          conditionalDefaultClassName(
            pluginClassName,
            conditionalKey,
            conditionalValue
          )
        );
      }
    }
  }
}
